import { z } from 'zod';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { ZodTransferInvestmentAccountType } from '@Common/types/bo/users/users';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodIDPathParam, ZodId, ZodStringIDPathParam } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodDateLike } from '@Common/types/zod/zod-date-like';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const boTransferStatuses = [
  'pending',
  'completed',
  'cancelled',
  'shares_canceled',
  'pending_approval',
  'rejected',
  'new',
  'failed',
  'submitted',
  'returned',
] as const;
export const ZodBoTransferStatus = z.enum(boTransferStatuses);
export type BoTransferStatus = z.infer<typeof ZodBoTransferStatus>;

export const ZodBoTransferType = z.enum([
  'card_top_up',
  'card_authorization',
  'card_withdrawal',
  'apple_pay_top_up',
  'deposit',
  'gift',
  'corporate_action',
  'subscription_refund',
  'withdrawal',
  'liquidation',
  'subscription',
  'refund_from_balance',
  'sepa_direct_debit_refund',
  'return',
  'checkout_chargeback',
  // Only ever returned when fetching a transfer by id, never listed
  'withdrawal_return',
  'operations_payment',
  'sepa_direct_debit',
] as const);

export type BoTransferType = z.infer<typeof ZodBoTransferType>;

export const getTransferMethods = ['card', 'bank', 'internal'] as const;
export const ZodGetTransferMethod = z.enum(getTransferMethods);
export type GetTransferMethod = z.infer<typeof ZodGetTransferMethod>;

export const ZodBoPaymentInitiatorType = z.enum(['user', 'shares']);
export type BoPaymentInitiatorType = z.infer<typeof ZodBoPaymentInitiatorType>;

export const MdlrAccountIntentValues = [
  'user',
  'user_savings',
  'user_pea',
  'buffer',
  'card_processing',
  'fees',
  'settlement',
  'subscription',
  'wm_fees',
  'b2b_settlement',
  'b2b_fees',
  'b2b_taxes',
  'b2b_company',
  'pea_buffer',
  'pea_card_processing',
  'pea_fees',
  'pea_settlement',
  'pea_wm_fees',
  'income_tax',
  'sepa_dd',
  'pea_sepa_dd',
  'pea_income_tax',
  'tec_taxes',
  'tec_fees',
  'tec_buffer',
  'tec',
  'coi_external',
  'buffer_partner',
  'buffer_oddo',
  'buffer_checkout',
  'buffer_shares',
  'buffer_unmatched_payment',
  'settlement_oddo',
  'pea_settlement_oddo',
  'user_per',
  'per_buffer',
  'per_card_processing',
  'per_fees',
  'per_settlement',
  'per_wm_fees',
  'per_sepa_dd',
  'per_income_tax',
  'per_settlement_oddo',
] as const;
export const ZodMdlrAccountIntent = z.enum(MdlrAccountIntentValues);
export type MdlrAccountIntent = z.infer<typeof ZodMdlrAccountIntent>;

export const boTransferBankLinkingTypes = ['open_banking', 'regular_banking', 'empty'] as const;
export const ZodBoTransferBankLinkingType = z.enum(boTransferBankLinkingTypes);
export type BoTransferBankLinkingType = z.infer<typeof ZodBoTransferBankLinkingType>;

export const PaymentSchemes = ['PI_SEPA_INST', 'PI_SECT', 'PI_SWIFT'] as const;
export const ZodPaymentScheme = z.enum(PaymentSchemes);
export type PaymentScheme = z.infer<typeof ZodPaymentScheme>;

export const ZodGetTransferResponse = z.object({
  id: ZodTrimmedString,
  user_id: ZodId.nullable(),
  type: ZodBoTransferType,
  amount: z.number().nullable(),
  currency: ZodTrimmedString.nullable(),
  status: ZodBoTransferStatus,
  created_at: ZodDateLike,
  updated_at: ZodDateLike,
  completed_at: ZodDateLike.nullable(),
  initiated_at: ZodDateLike.nullable(),
  mdlr_status: ZodTrimmedString.nullable(),
  mdlr_payment_id: ZodId.nullable(),
  bank_account_holder_name: ZodTrimmedString.nullable(),
  bank_name: z.union([ZodTrimmedString.nullable(), z.literal('')]),
  bank_account_number: ZodTrimmedString.nullable(),
  method: ZodGetTransferMethod,
  card_network: ZodTrimmedString.nullable(),
  card_account_last_digits: ZodTrimmedString.nullable(),
  card_checkout_token: ZodTrimmedString.nullable(),
  card_holder_name: ZodTrimmedString.nullable(),
  card_transfer_ip_address: ZodTrimmedString.nullable(),
  decline_summary: ZodTrimmedString.nullable(),
  failure_reason: ZodTrimmedString.nullable(),
  review_request_id: ZodId.nullable(),
  b2b_review_request_id: ZodId.nullable(),
  is_cancellable: z.boolean(),
  is_retryable: z.boolean(),
  // What a retry would send, which is short of the withdrawal when a scheme fee went uncovered
  retry_amount: z.number().nullable(),
  available_payment_schemes: z.array(ZodPaymentScheme),
  bank_linking_type: ZodBoTransferBankLinkingType.nullable(),
  estimated_available_date: ZodTrimmedString.nullable(),
  sepa_instant_support: z.boolean().nullable(),
  co_status: ZodTrimmedString.nullable(),
  checkout_id: ZodTrimmedString.nullable(),
  mdlr_account_intent: ZodMdlrAccountIntent.nullable(),
  beneficiary_id: ZodId.nullable(),
  company_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
  employee_id: ZodId.nullable(),
  od_order_ids: z.array(ZodId).nullable(),
  initiator: z.enum(['user', 'shares']).nullable(),
  investment_account_type: ZodTransferInvestmentAccountType,
  investment_plan_id: ZodId.nullable(),
  triggered_order_id: ZodId.nullable(),
  is_internal: z.boolean(),
  reference: ZodTrimmedString.nullable(),
  remittance_info: ZodTrimmedString.nullable(),
  mdlr_transfer_type: ZodTrimmedString.nullable(),
  description: ZodTrimmedString.nullable(),
  e2e_reference: ZodTrimmedString.nullable(),
  payment_batch_external_id: ZodTrimmedString.nullable(),
  approval_check: z.array(ZodTrimmedString),
  approval_status: ZodTrimmedString.nullable(),
  b2b_withdrawal_id: ZodId.nullable(),
  b2b_campaign_payout_id: ZodId.nullable(),
  b2b_campaign_id: ZodId.nullable(),
  b2b_individual_operation_id: ZodId.nullable(),
  source_id: ZodId.nullable(),
  // `mdlr_payments.id` of the return this withdrawal came back as
  withdrawal_return_payment_id: ZodId.nullable(),
  // What the scheme actually sent back, when the withdrawal was returned
  returned_amount: z.number().nullable(),
  // Scheme fee kept on a fee-short return and who absorbed it. Stored on the return row,
  // also reported on the withdrawal that return belongs to.
  payment_scheme_fee_amount: z.number().nullable(),
  is_payment_scheme_fee_covered: z.boolean().nullable(),
  // `co_payments.id` of the payment this refund is refunding
  refunded_payment_id: ZodId.nullable(),
  // BO transfer id of that payment, so the refund can link back to it
  refunded_transfer_id: ZodTrimmedString.nullable(),
  /**
   * Earliest date the SEPA DD refund can be sent to Checkout.com. Only set while the
   * refund is still waiting. The trigger runs once a day, so the refund may go out later
   * than this, never before it.
   */
  refund_earliest_execution_date: ZodDateLike.nullable(),
});

export type GetTransferResponse = z.infer<typeof ZodGetTransferResponse>;

const ZodCancelTransferResponse = z.object({
  id: ZodTrimmedString,
  status: ZodBoTransferStatus,
});
export type CancelTransferResponse = z.infer<typeof ZodCancelTransferResponse>;

const ZodCancelTransferRequestBody = z.object({
  reason: ZodTrimmedString.min(6),
});

export type CancelTransferRequestBody = z.infer<typeof ZodCancelTransferRequestBody>;

export const ZodGetTransfersItemResponse = z.object({
  id: ZodTrimmedString,
  user_id: ZodId.nullable(),
  type: ZodBoTransferType,
  amount: z.number().nullable(),
  currency: ZodTrimmedString.nullable(),
  status: ZodBoTransferStatus,
  created_at: ZodDateLike,
  updated_at: ZodDateLike,
  completed_at: ZodDateLike.nullable(),
  co_status: ZodTrimmedString.nullable(),
  checkout_id: ZodTrimmedString.nullable(),
  mdlr_status: ZodTrimmedString.nullable(),
  settlement_id: ZodId.nullable(),
  withdrawal_settlement_id: ZodId.nullable(),
  bank_linking_type: ZodBoTransferBankLinkingType.nullable(),
  mdlr_account_intent: ZodMdlrAccountIntent.nullable(),
  account_number: ZodTrimmedString.nullable(),
  account_holder_name: ZodTrimmedString.nullable(),
  investment_account_type: ZodTransferInvestmentAccountType,
  triggered_order_id: ZodId.nullable(),
  investment_plan_id: ZodId.nullable(),
  company_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
  employee_id: ZodId.nullable(),
  od_order_ids: z.array(ZodId).nullable(),
  b2b_withdrawal_id: ZodId.nullable(),
  b2b_campaign_payout_id: ZodId.nullable(),
  b2b_campaign_id: ZodId.nullable(),
  b2b_individual_operation_id: ZodId.nullable(),
  is_internal: z.boolean(),
});

export type GetTransfersItemResponse = z.infer<typeof ZodGetTransfersItemResponse>;

const ZodGetTransfersResponse = z.array(ZodGetTransfersItemResponse);

export type GetTransfersResponse = z.infer<typeof ZodGetTransfersResponse>;

export const ZodGetTransfersQueryFilers = z
  .object({
    id: createZodArrayable(ZodTrimmedString),
    user_id: createZodArrayable(ZodId),
    type: createZodArrayable(ZodTrimmedString),
    amount: createZodArrayable(z.number()),
    from_amount: createZodArrayable(z.number()),
    to_amount: createZodArrayable(z.number()),
    currency: createZodArrayable(ZodTrimmedString),
    from_created_at: createZodArrayable(ZodTrimmedString),
    to_created_at: createZodArrayable(ZodTrimmedString),
    to_completed_at: createZodArrayable(ZodTrimmedString),
    status: createZodArrayable(ZodBoTransferStatus),
    mdlr_status: createZodArrayable(ZodTrimmedString),
    settlement_id: createZodArrayable(ZodId),
    investment_account_type: createZodArrayable(ZodTransferInvestmentAccountType),
    withdrawal_settlement_id: createZodArrayable(ZodId),
    bank_linking_type: createZodArrayable(ZodBoTransferBankLinkingType),
    mdlr_account_intent: createZodArrayable(ZodMdlrAccountIntent),
    checkout_id: createZodArrayable(ZodTrimmedString),
    b2b_withdrawal_id: createZodArrayable(ZodId),
    company_id: createZodArrayable(ZodId),
    b2b_company_id: createZodArrayable(ZodId),
    employee_id: createZodArrayable(ZodId),
    b2b_campaign_id: createZodArrayable(ZodId),
    b2b_individual_operation_id: createZodArrayable(ZodId),
    savings_funds_request_ids: createZodArrayable(ZodId),
  })
  .partial();

export const getTransfersQuerySortColumns: (keyof GetTransfersItemResponse)[] = [
  'created_at',
  'updated_at',
  'completed_at',
];

const ZodGetTransfersQuery = buildZodBoListQueryParsed(
  ZodGetTransfersQueryFilers,
  getTransfersQuerySortColumns,
  {
    range: [0, 100],
  },
);

export type GetTransfersQuery = BoListQueryBase<
  z.infer<typeof ZodGetTransfersQueryFilers>,
  (typeof getTransfersQuerySortColumns)[number]
>;

export const getTransfersEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetTransfersQuery,
  responseBody: ZodGetTransfersResponse,
});

export type GetTransfersEndpoint = GetEndpointType<typeof getTransfersEndpoint>;

export const getTransfersIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodStringIDPathParam,
  responseBody: ZodGetTransferResponse,
});

export type GetTransfersIdEndpoint = GetEndpointType<typeof getTransfersIdEndpoint>;

export const putTransfersCancelIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodStringIDPathParam,
  body: ZodCancelTransferRequestBody,
  responseBody: ZodCancelTransferResponse,
});

export type PutTransfersCancelIdEndpoint = GetEndpointType<typeof putTransfersCancelIdEndpoint>;

const ZodRetryPaymentResponse = z.object({
  id: ZodTrimmedString,
});
export type RetryPaymentResponse = z.infer<typeof ZodRetryPaymentResponse>;

const ZodRetryPaymentRequestBody = z.object({
  payment_scheme: ZodPaymentScheme,
});
export type RetryPaymentRequestBody = z.infer<typeof ZodRetryPaymentRequestBody>;

export const putTransfersRetryIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodStringIDPathParam,
  body: ZodRetryPaymentRequestBody,
  responseBody: ZodRetryPaymentResponse,
});

export type PutTransfersRetryIdEndpoint = GetEndpointType<typeof putTransfersRetryIdEndpoint>;

const ZodGetTransfersSummaryResponse = z.object({
  id: ZodId,
  currency: ZodTrimmedString,
  total_deposits_amount: z.number(),
  total_card_deposits_amount: z.number(),
  total_bank_deposits_amount: z.number(),
  total_withdrawals_amount: z.number(),
});

export type GetTransfersSummaryResponse = z.infer<typeof ZodGetTransfersSummaryResponse>;

export const getTransfersSummaryEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetTransfersSummaryResponse,
});

export type GetTransfersSummaryEndpoint = GetEndpointType<typeof getTransfersSummaryEndpoint>;

export const ZodGetTransferRetryItemResponse = z.object({
  id: ZodId,
  mdlr_payment_id: ZodId,
  beneficiary_id: ZodId.nullable(),
  payment_scheme: ZodTrimmedString.nullable(),
  status: ZodTrimmedString.nullable(),
  failure_code: ZodTrimmedString.nullable(),
  failure_reason: ZodTrimmedString.nullable(),
  failure_http_code: z.number().nullable(),
  e2e_reference: ZodTrimmedString.nullable(),
  request_nonce: ZodTrimmedString.nullable(),
  mdlr_id: ZodTrimmedString.nullable(),
  initiated_at: ZodDateLike.nullable(),
  executed_at: ZodDateLike.nullable(),
  created_at: ZodDateLike,
});

export type GetTransferRetryItemResponse = z.infer<typeof ZodGetTransferRetryItemResponse>;

const ZodGetTransferRetriesResponse = z.array(ZodGetTransferRetryItemResponse);

export type GetTransferRetriesResponse = z.infer<typeof ZodGetTransferRetriesResponse>;

export const ZodGetTransferRetriesQueryFilters = z
  .object({
    mdlr_payment_id: createZodArrayable(ZodId),
  })
  .partial();

export const getTransferRetriesSortColumns: (keyof GetTransferRetryItemResponse)[] = [
  'created_at',
  'id',
];

const ZodGetTransferRetriesQuery = buildZodBoListQueryParsed(
  ZodGetTransferRetriesQueryFilters,
  getTransferRetriesSortColumns,
  {
    range: [0, 24],
  },
);

export type GetTransferRetriesQuery = BoListQueryBase<
  z.infer<typeof ZodGetTransferRetriesQueryFilters>,
  (typeof getTransferRetriesSortColumns)[number]
>;

export const getTransfersRetriesEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetTransferRetriesQuery,
  responseBody: ZodGetTransferRetriesResponse,
});

export type GetTransfersRetriesEndpoint = GetEndpointType<typeof getTransfersRetriesEndpoint>;

export const ZodGetTransferAttemptItemResponse = z.object({
  id: ZodId,
  mdlr_payment_id: ZodId,
  amount: z.number(),
  currency: ZodTrimmedString,
  status: ZodTrimmedString.nullable(),
  mdlr_transfer_type: ZodTrimmedString.nullable(),
  beneficiary_id: ZodId.nullable(),
  failure_reason: ZodTrimmedString.nullable(),
  created_at: ZodDateLike,
  initiated_at: ZodDateLike.nullable(),
  executed_at: ZodDateLike.nullable(),
  returned_amount: z.number().nullable(),
  payment_scheme_fee_amount: z.number().nullable(),
  is_payment_scheme_fee_covered: z.boolean().nullable(),
});

export type GetTransferAttemptItemResponse = z.infer<typeof ZodGetTransferAttemptItemResponse>;

const ZodGetTransferAttemptsResponse = z.array(ZodGetTransferAttemptItemResponse);

export type GetTransferAttemptsResponse = z.infer<typeof ZodGetTransferAttemptsResponse>;

export const ZodGetTransferAttemptsQueryFilters = z
  .object({
    mdlr_payment_id: createZodArrayable(ZodId),
  })
  .partial();

export const getTransferAttemptsSortColumns: (keyof GetTransferAttemptItemResponse)[] = ['id'];

const ZodGetTransferAttemptsQuery = buildZodBoListQueryParsed(
  ZodGetTransferAttemptsQueryFilters,
  getTransferAttemptsSortColumns,
  {
    range: [0, 24],
  },
);

export type GetTransferAttemptsQuery = BoListQueryBase<
  z.infer<typeof ZodGetTransferAttemptsQueryFilters>,
  (typeof getTransferAttemptsSortColumns)[number]
>;

export const getTransfersAttemptsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetTransferAttemptsQuery,
  responseBody: ZodGetTransferAttemptsResponse,
});

export type GetTransfersAttemptsEndpoint = GetEndpointType<typeof getTransfersAttemptsEndpoint>;
